import Editor from "./Editor";
import Debugger from "./Debugger";
import { useState, useRef, useEffect } from "react";
import { usePlutoFrame } from "@plutoxyz/react-frame";
import LLM, { LLMHandle } from "./LLM";
import EditorToggle from "./EditorToggle";

type TabId = "editor" | "chat";

const EditorContainer = ({
  onUpdate,
  hasChanges,
  onHasChanges,
  editorRef,
  setLastChangeHadSpace,
}: {
  onUpdate: (code: string) => void;
  hasChanges: boolean;
  onHasChanges: (hasChanges: boolean) => void;
  editorRef: React.RefObject<any>;
  setLastChangeHadSpace: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { isReady } = usePlutoFrame();

  const [code, setCode] = useState<string>("");
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState<number>(0);
  const [debuggerHeight, setDebuggerHeight] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<TabId>("editor");
  const [resetKey, setResetKey] = useState<number>(0);
  const [frameError, setFrameError] = useState<string | null>(null);

  const llmRef = useRef<LLMHandle>(null);

  // Create a ref to access Editor methods
  const editorHostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorContainerRef.current) {
      setEditorHeight(editorContainerRef.current.clientHeight);
    }
  }, [editorContainerRef.current]);

  // Update editor height when window is resized
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout | null = null;

    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        if (editorContainerRef.current) {
          setEditorHeight(editorContainerRef.current.clientHeight);
        }
      }, 50);
    };

    window.addEventListener("resize", handleResize);

    // Initial height calculation
    handleResize();

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, []);

  const handleCodeGenerated = (generatedCode: string) => {
    setCode(generatedCode);
    onUpdate(generatedCode);
    setLastChangeHadSpace((p) => !p);
    onHasChanges(false);
    setFrameError(null);
  };

  const handleScriptChange = async (script: string) => {
    await editorRef.current?.changeScript?.(script);
  };

  const commonEditorLlMContainerStyles = "flex flex-grow flex-col relative";

  return (
    <div className="flex flex-col flex-grow min-w-0 border-x border-t border-gray-100 rounded-2xl">
      {/* Tab Selector */}

      <EditorToggle
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        hasChanges={hasChanges}
        onScriptChange={handleScriptChange}
        editorRef={editorRef}
      />

      {/* Content Area: Editor and LLM always rendered, visibility controlled by activeTab */}
      {/* Editor Tab Content */}
      <div
        ref={editorHostRef} // Ref for editor's direct parent
        className={`relative   ${commonEditorLlMContainerStyles} ${activeTab === "editor" ? "flex" : "hidden"}`}
      >
        <Editor
          key={isReady ? "ready" : "not-ready"}
          onUpdate={(code) => {
            setCode(code);
            onUpdate(code);
          }}
          setLastChangeHadSpace={setLastChangeHadSpace}
          onHasChanges={onHasChanges}
          hasChanges={hasChanges}
          editorHeight={Math.max(
            editorHeight - (debuggerHeight || 30),
            document.documentElement.clientHeight - 160,
          )}
          editorRef={editorRef}
        />
        <Debugger onHeightChange={setDebuggerHeight} />
      </div>

      {/* Chat Tab Content */}
      <div
        className={`${commonEditorLlMContainerStyles} overflow-x-hidden min-h-0 ${activeTab === "chat" ? "flex" : "hidden"}`}
      >
        <LLM
          key={resetKey} // resetKey is static (0), so LLM won't remount due to key change on tab switch
          ref={llmRef}
          onGenerateCode={handleCodeGenerated}
          onInputChange={() => onHasChanges(true)}
          error={frameError}
          onClearError={() => setFrameError(null)}
        />
      </div>
    </div>
  );
};

export default EditorContainer;
